import type { CartItem } from '#types/cart'

/**
 * Resolves SSR-unsafe filter state (currently `cart.items`) safely across SSR and the client's
 * first hydration render.
 *
 * @remarks
 * Reading `cart.items` directly during `setup()` can already differ between the server and the
 * client at the same logical render - the server may hold real data (e.g. an ancestor page
 * `await`ed `cart.get()` during SSR), while the client reflects whatever was restored
 * client-side - which triggers a hydration mismatch for any markup that depends on it.
 * `onMounted` is the one hook Vue guarantees never runs during SSR (an immediate `watch` still
 * runs synchronously during SSR regardless of its `flush` option), so this composable defers to
 * the resolved cart only after the client has mounted, then keeps it live for later cart changes.
 * @param hasUnsafeFilter - Whether the caller's content actually depends on SSR-unsafe filter
 * state. When `false`, nothing is deferred - `cartItems` stays `undefined` and `showSkeleton`
 * stays `false`.
 * @returns `cartItems` - `undefined` until resolved client-side, then the live cart items - and
 * `showSkeleton` - `true` until that first resolution has happened. Extend with further
 * SSR-unsafe filter state (e.g. geolocation) here as new filter types are added.
 * @category Composables
 */
export function useSsrSafeFilterState(hasUnsafeFilter: boolean) {
  const cart = useCartStore()
  const cartItems = ref<CartItem[]>()
  const showSkeleton = ref(hasUnsafeFilter)

  if (hasUnsafeFilter) {
    onMounted(() => {
      cartItems.value = cart.items
      showSkeleton.value = false
    })
    watch(() => cart.items, (items) => {
      cartItems.value = items
    })
  }

  return { cartItems, showSkeleton }
}
